import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useOutletContext } from 'react-router';

import { useSelector } from 'hooks';
import useRouterSteps from 'hooks/useRouterSteps';
import { getSelectedSelfService } from 'modules/selfServices/selectors';
import type PageBaseProps from 'types/PageBase';

import PageHeader from 'components/ui/PageHeader';
import type { SelfServiceStep } from '../SelfServices/types';

export interface ReopenOutletContext {
  steps: SelfServiceStep[];
  onBoundary: (end?: boolean) => void;
}

const ReopenStepContent: React.FC = () => {
  const { steps, onBoundary } = useOutletContext<ReopenOutletContext>();
  const selfService = useSelector(getSelectedSelfService);

  const {
    currentStep,
    next,
    previous,
  } = useRouterSteps<unknown, PageBaseProps>({ steps });

  const currentIndex = steps.findIndex(({ key }) => key === currentStep?.key);
  const isLastStep = currentIndex === steps.length - 1;
  const isFirstStep = currentIndex === 0;

  const handleNext = React.useCallback(() => {
    if (isLastStep) {
      onBoundary?.(true);
      return;
    }
    next();
  }, [isLastStep, onBoundary, next]);

  const handlePrev = React.useCallback(() => {
    if (isFirstStep) {
      onBoundary?.(false);
      return;
    }
    previous();
  }, [isFirstStep, onBoundary, previous]);

  const isPenultimate = React.useMemo(
    () => currentIndex === steps.length - 2,
    [currentIndex, steps.length],
  );

  const Component = React.useMemo(
    () => ('render' in currentStep ? currentStep.render : currentStep.component),
    [currentStep],
  );

  const titleMessage = React.useMemo(() => {
    if (currentStep?.title) {
      return typeof currentStep.title === 'function'
        ? currentStep.title(selfService)
        : currentStep.title;
    }
    return undefined;
  }, [currentStep, selfService]);

  if (!Component) {
    return null;
  }

  return (
    <>
      {!currentStep.data?.hideHeader && (
        <PageHeader
          onPrev={handlePrev}
          length={steps.length}
          currentIndex={currentIndex}
          hideSteps={currentStep.data?.hideStepIndicator}
          hidePrevButton={currentStep.data?.hidePrevButton}
          title={titleMessage ? <FormattedMessage {...titleMessage} /> : undefined}
        />
      )}
      <Component
        onNext={handleNext}
        onPrev={handlePrev}
        isPenultimate={isPenultimate}
        shouldDisplayBackButton={currentIndex > 0}
      />
    </>
  );
};

export default ReopenStepContent;
